import { useState, useEffect, useCallback } from "react";
import { useTagApi } from "../../hooks/useTagApi";

export const useAllTagsViewModel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  const {
    getTags,
    deleteTag,
    createTag,
    updateTag: apiUpdateTag,
    cacheOperations: { readTags, writeTags },
  } = useTagApi();

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const tagsResult = await getTags();
      if (!tagsResult.success) {
        throw new Error("Failed to fetch tags");
      }
      writeTags(tagsResult.data);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [getTags, writeTags]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateTag = useCallback(
    async (formData) => {
      try {
        const result = await createTag(formData);
        if (result.success) {
          setSuccessModal("Tag created successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error creating tag in View Model:", error);
        throw new Error(error.message);
      }
    },
    [createTag]
  );

  const handleUpdateTag = useCallback(
    async (tagId, formData) => {
      try {
        const result = await apiUpdateTag(tagId, formData);
        if (result.success) {
          setSuccessModal("Tag updated successfully");
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      } catch (error) {
        console.error("Error updating tag:", error);
        throw new Error(error.message);
      }
    },
    [apiUpdateTag]
  );

  const handleDelete = useCallback(
    async (tag) => {
      try {
        const result = await deleteTag(tag.tag_id);
        if (result.success) {
          setSuccessModal({
            type: "success",
            title: "Success",
            message: "Tag deleted successfully",
          });
        } else {
          setSuccessModal({
            type: "error",
            title: "Action Failed",
            message: result.error || "Failed to delete client",
          });
        }
      } catch (err) {
        setError(err.message);
        console.error("Error deleting tag:", err);
      }
    },
    [deleteTag]
  );

  return {
    tags: readTags(),
    createTag: handleCreateTag,
    updateTag: handleUpdateTag,
    handleDelete,
    successModal,
    setSuccessModal,
  };
};
